import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../auth/AuthProvider';
import { useI18n } from '../../i18n/I18nProvider';
import { AccountImportDialog } from './AccountImportDialog';
import { AccountsView } from './AccountsView';
import { createAccount, createJsonFetch, importChart, loadAccountsTree } from './accounts.api';
import type { AccountNode, ChartImportReport, CreateAccountInput } from './accounts.api';

/** CSV is text; an `.xlsx` workbook is binary and only the base64 form survives JSON. */
async function readChartFile(file: File): Promise<{ content: string; encoding?: 'base64' }> {
  if (file.name.toLowerCase().endsWith('.csv')) {
    return { content: await file.text() };
  }
  const bytes = new Uint8Array(await file.arrayBuffer());
  let binary = '';
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }
  return { content: btoa(binary), encoding: 'base64' };
}

export default function AccountsScreen(): JSX.Element {
  const { authorizedFetch } = useAuth();
  const { t } = useI18n();
  const [tree, setTree] = useState<AccountNode[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const [report, setReport] = useState<ChartImportReport | null>(null);
  const [fileName, setFileName] = useState<string | undefined>(undefined);
  const [importError, setImportError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const fetchJson = useMemo(() => createJsonFetch(authorizedFetch), [authorizedFetch]);

  const reload = useCallback(async (): Promise<void> => {
    try {
      setTree(await loadAccountsTree(fetchJson));
      setLoadError(null);
    } catch (error) {
      setLoadError(error instanceof Error ? error.message : String(error));
    }
  }, [fetchJson]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const runImport = async (file: File, dryRun: boolean): Promise<void> => {
    setBusy(true);
    setImportError(null);
    try {
      const payload = await readChartFile(file);
      setFileName(file.name);
      const result = await importChart(fetchJson, { ...payload, fileName: file.name }, dryRun);
      setReport(result);
      if (!dryRun) {
        await reload();
        if (result.errors.length === 0) setImportOpen(false);
      }
    } catch (error) {
      setImportError(error instanceof Error ? error.message : String(error));
    } finally {
      setBusy(false);
    }
  };

  const create = async (input: CreateAccountInput): Promise<void> => {
    setBusy(true);
    try {
      await createAccount(fetchJson, input);
      await reload();
    } finally {
      setBusy(false);
    }
  };

  if (tree === null) {
    return (
      <section className="card" data-testid="accounts-loading">
        {loadError === null ? <p className="muted">{t('accounts.loading')}</p> : <p className="alert alert--danger">{loadError}</p>}
      </section>
    );
  }

  return (
    <>
      {loadError === null ? null : (
        <p className="alert alert--danger" role="alert">
          {loadError}
        </p>
      )}
      <AccountsView
        tree={tree}
        busy={busy}
        onImportRequest={() => {
          setReport(null);
          setImportError(null);
          setImportOpen(true);
        }}
        onCreate={create}
      />
      <AccountImportDialog
        open={importOpen}
        report={report}
        fileName={fileName}
        error={importError}
        busy={busy}
        onClose={() => setImportOpen(false)}
        onCheck={(file) => void runImport(file, true)}
        onApply={(file) => void runImport(file, false)}
      />
    </>
  );
}
